/**
 * Il renderer di un campo (CF02/05) — CL04.
 *
 * Uno solo, per tutte le superfici: se ogni schermata avesse il suo, un tipo
 * nuovo comparirebbe su alcune e non su altre, e nessuno saprebbe quale sia
 * quello giusto.
 *
 * Quattro scelte che si vedono nel codice:
 *
 * - **I numeri restano stringhe canoniche.** `type="number"` di un `<input>`
 *   passa per un `double`: un importo con otto decimali torna indietro
 *   arrotondato, e l'arrotondamento non si annulla. Si usa un campo di testo con
 *   `inputMode="decimal"` e `parseDecimal`, che lavora sulla grafia. Mentre il
 *   campo è a fuoco si mostra **ciò che si digita**, e il formato del campo si
 *   applica a riposo: riformattare a ogni tasto muove il caret in fondo e
 *   riscrive sotto le dita quello che l'utente ha appena scritto.
 * - **La data è di un picker, non dell'`input` nativo.** `displayFormat` era
 *   ignorato e la digitazione a mano scomoda: `DatePicker`/`DateTimePicker` di
 *   MUI X (già montati con il loro provider) mostrano `dd/mm/yyyy` e si
 *   digitano. Il contratto del valore non cambia: `YYYY-MM-DD` per una data
 *   pura, ISO completo per un istante.
 * - **Vuoto non è zero.** Il renderer precedente faceva `value || 0`, quindi un
 *   campo mai compilato mostrava `0` e `false`/`0` diventavano indistinguibili
 *   da "non compilato". Qui `null` è vuoto, e resta vuoto.
 * - **Il calcolato e il progressivo non si scrivono.** Li produce il server; un
 *   campo modificabile che il server poi sovrascrive è una bugia che l'utente
 *   scopre al salvataggio successivo.
 * - **`access` viene dal contesto, non dal tipo.** È il server a dire se questo
 *   operatore può scrivere: `readonly` qui significa che non si scrive, anche se
 *   il tipo sarebbe editabile altrove.
 */

import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import React from "react";
import dayjs from "dayjs";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { FieldDefinition, FieldValue, OptionDefinition, RuntimeField } from "model/CustomFields";
import {
  displayValue,
  formatDecimal,
  formatPercent,
  parseDecimal,
  parsePercent,
  type DecimalFormat,
} from "./valueFormat";
import AttachmentFieldInput from "./AttachmentFieldInput";
import type { AttachmentHost } from "./attachmentField";
import { CALCULATED_MISSING_DATA_KEY, textForCalculatedValue } from "./userdata";
import { symbolFor } from "./currencies";

interface CustomFieldInputProps {
  field: RuntimeField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  disabled?: boolean;
  /**
   * Come caricare un allegato, dove si può.
   *
   * Lo mette la superficie che ha il task (vedi `AttachmentHost`): il
   * caricamento passa dal token dell'istanza del flownodo, che il contesto del
   * contratto non porta. Senza, il campo allegato è in sola lettura.
   */
  attachments?: AttachmentHost | null;
  /**
   * Il messaggio d'errore già risolto per questo campo, o `null`/assente.
   *
   * Lo produce la superficie che ha i `fieldErrors` del server (es. 422
   * `VALIDATION_FAILED`): senza, il campo incriminato non si distinguerebbe
   * dagli altri. Opzionale, quindi retro-compatibile: assente → niente cambia.
   */
  error?: string | null;
}

/**
 * Il formato decimale di un campo, dal suo contratto.
 *
 * La `precision` è quella dichiarata dal campo: mostrare più cifre di quante ne
 * siano conservate prometterebbe una precisione che il valore non ha, e
 * mostrarne meno nasconderebbe cifre che ci sono.
 */
const decimalFormatOf = (definition: FieldDefinition): DecimalFormat => ({
  precision: (definition.config?.precision as number) ?? 2,
  decimalSeparator: (definition.config?.decimalSeparator as string) ?? ".",
  thousandsSeparator: (definition.config?.thousandsSeparator as string) ?? "",
  // Il simbolo puo' mancare su un campo configurato prima della lista valute:
  // si ricava dal codice (es. EUR -> €) invece di mostrare l'importo nudo.
  symbol:
    (definition.config?.symbol as string) ||
    symbolFor(definition.config?.currencyCode as string) ||
    null,
  symbolPosition:
    (definition.config?.symbolPosition as "before" | "after") ?? "before",
});

const optionsOf = (definition: FieldDefinition): OptionDefinition[] =>
  Array.isArray(definition.config?.options)
    ? (definition.config.options as OptionDefinition[])
    : [];

const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  attachments,
  error = null,
}) => {
  const translations = useTranslations();
  const { definition, access } = field;
  const readOnly = disabled || access !== "editable";
  const testId = `cf-value-${definition.fieldId}`;
  const label = definition.label || getTranslation(translations, "placeholder.value.label");
  /**
   * Il valore c'e' davvero. Non e' un `Boolean(value)`: `false` e `0` sono valori
   * compilati, non assenze, e trattarli come vuoti nasconderebbe un dato — la
   * stessa distinzione che il trasporto dei valori fa altrove.
   */
  const hasValue = value !== null && value !== undefined && value !== "";

  /**
   * Il testo che l'utente sta digitando in un campo decimale, mentre è a fuoco.
   *
   * `null` = non si sta digitando: a schermo va il valore formattato. Mentre si
   * digita va la grafia dell'utente, senza riformattarla a ogni tasto.
   */
  const [draft, setDraft] = React.useState<string | null>(null);

  /**
   * Il segnaposto dei campi di testo.
   *
   * `getTranslation` restituisce **la chiave** quando la voce manca, quindi il
   * testo italiano va scritto qui e si riconosce la chiave non tradotta: senza
   * questo, a schermo si leggerebbe `placeholder.text.caption` finché qualcuno
   * non inserisce la riga nel dizionario.
   */
  const segnaposto = ((): string => {
    const chiave = "placeholder.text.caption";
    const risolta = getTranslation(translations, chiave);
    return !risolta || risolta === chiave ? "Inserisci testo…" : risolta;
  })();

  const common = {
    size: "small" as const,
    fullWidth: true,
    label,
    disabled: readOnly,
    onKeyUp: (e: React.KeyboardEvent) => e.stopPropagation(),
    // La label sta **sopra** il campo, sempre, come nel progressivo: dentro il
    // riquadro finisce sopra il valore quando c'è (l'unico testo che si legge è
    // il dato, con l'etichetta stampata addosso) e copre il segnaposto quando non
    // c'è. Un `disabled` non prende il focus, quindi MUI non la fa salire da
    // sola: va forzata anche quando il campo è vuoto.
    InputLabelProps: { shrink: true },
    // L'errore del server per questo campo: `TextField` lo rende in rosso e al
    // posto dell'helper. La `textarea` sovrascrive `helperText` sotto, per non
    // perdere il contatore quando non c'è errore.
    error: Boolean(error),
    helperText: error ?? undefined,
  };

  // Un controllo senza `helperText` proprio (checkbox, radio, autocomplete,
  // allegato): l'errore si rende a mano sotto, con la stessa resa del TextField.
  const errorBelow = error ? (
    <Typography color="error" variant="caption" component="div">
      {error}
    </Typography>
  ) : null;

  switch (definition.type) {
    // I tipi di testo: cambia la validazione che il server applica, non il
    // controllo con cui si scrivono.
    case "text":
    case "email":
    case "url":
    case "phone": {
      const maxLength = definition.config?.maxLength as number | undefined;
      return (
        <TextField
          {...common}
          value={typeof value === "string" ? value : ""}
          // Il segnaposto dice cosa si scrive, non ripete la label: la label è
          // già sopra, e "Inserisci testo…" in un campo vuoto è l'unico invito
          // che serve. Con un valore presente sparirebbe da sé.
          placeholder={hasValue ? undefined : segnaposto}
          inputProps={{ "data-testid": testId, maxLength }}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }

    case "textarea": {
      const rows = (definition.config?.rows as number) ?? 3;
      const maxLength = definition.config?.maxLength as number | undefined;
      const showCounter = definition.config?.showCounter === true;
      const current = typeof value === "string" ? value : "";

      return (
        <TextField
          {...common}
          multiline
          minRows={rows}
          value={current}
          placeholder={hasValue ? undefined : segnaposto}
          inputProps={{ "data-testid": testId, maxLength }}
          onChange={(e) => onChange(e.target.value)}
          // Il contatore si mostra solo se richiesto: un limite non dichiarato
          // che tronca è peggio di nessun limite. Se c'è un errore vince lui:
          // sotto il campo c'è posto per una riga sola.
          helperText={error ?? (showCounter ? `${current.length}/${maxLength ?? "—"}` : undefined)}
        />
      );
    }

    // I decimali: stringa canonica, mai un `double`.
    case "number":
    case "currency": {
      const format = decimalFormatOf(definition);
      const precision = definition.config?.precision as number | undefined;
      const shown = typeof value === "string" ? formatDecimal(value, format) : "";
      return (
        <TextField
          {...common}
          // `type="number"` no: passerebbe per un `double`.
          inputMode="decimal"
          // A riposo il canonico nel formato del campo, a fuoco la grafia
          // dell'utente: il formato si riapplica al blur, non a ogni tasto.
          value={draft ?? shown}
          inputProps={{ "data-testid": testId }}
          onFocus={() => setDraft(shown)}
          onBlur={() => setDraft(null)}
          onChange={(e) => {
            const text = e.target.value;
            setDraft(text);
            const parsed = parseDecimal(text, format);
            // `null` mentre si digita ("1,") non è un valore da mandare: si
            // conserva la grafia e si lascia che il server giudichi al salvataggio.
            onChange(parsed === null ? text : parsed);
          }}
          placeholder={precision !== undefined ? `0.${"0".repeat(precision)}` : "0.00"}
        />
      );
    }

    case "percent": {
      const factor = (definition.config?.displayFactor as string) ?? "1";
      const format = decimalFormatOf(definition);
      const shown = typeof value === "string" ? formatPercent(value, factor, format) : "";
      return (
        <TextField
          {...common}
          inputMode="decimal"
          value={draft ?? shown}
          inputProps={{ "data-testid": testId }}
          onFocus={() => setDraft(shown)}
          onBlur={() => setDraft(null)}
          onChange={(e) => {
            const text = e.target.value;
            setDraft(text);
            const parsed = parsePercent(text, factor, format);
            onChange(parsed === null ? text : parsed);
          }}
          placeholder="0"
        />
      );
    }

    case "date": {
      const includeTime = definition.config?.includeTime === true;

      // Il picker di MUI X, non l'`input` nativo: il nativo ignorava
      // `displayFormat`, non si digita comodamente e non si apre il calendario
      // dove ci si aspetta. Il contratto del valore resta: `YYYY-MM-DD` per una
      // data pura, un istante **con offset** quando c'è l'orario — `format()` di
      // dayjs lo porta (l'offset lo conosce il browser).
      const parsed = typeof value === "string" ? dayjs(value) : null;
      const current = parsed && parsed.isValid() ? parsed : null;
      const handleChange = (selected: dayjs.Dayjs | null) =>
        onChange(
          selected && selected.isValid()
            ? includeTime
              ? selected.format()
              : selected.format("YYYY-MM-DD")
            : null
        );
      // Stesso appiglio degli altri campi, `cf-value-<id>`, sull'input interno:
      // è lì che i test e le superfici lo cercano.
      const textField = { ...common, inputProps: { "data-testid": testId } };
      return includeTime ? (
        <DateTimePicker
          value={current}
          onChange={handleChange}
          format="DD/MM/YYYY HH:mm"
          ampm={false}
          disabled={readOnly}
          slotProps={{ textField }}
        />
      ) : (
        <DatePicker
          value={current}
          onChange={handleChange}
          format="DD/MM/YYYY"
          disabled={readOnly}
          slotProps={{ textField }}
        />
      );
    }

    case "boolean": {
      const trueLabel = (definition.config?.trueLabel as string) ?? label;
      const falseLabel = (definition.config?.falseLabel as string) ?? label;
      return (
        <>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                inputProps={{ "data-testid": testId } as React.InputHTMLAttributes<HTMLInputElement>}
                checked={value === true}
                disabled={readOnly}
                onChange={(e, checked) => onChange(checked)}
              />
            }
            label={value === true ? trueLabel : falseLabel}
          />
          {errorBelow}
        </>
      );
    }

    case "radio":
      return (
        <>
          <RadioGroup
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          >
            {optionsOf(definition).map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                disabled={readOnly}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {errorBelow}
        </>
      );

    case "select":
      return (
        <TextField
          {...common}
          select
          SelectProps={{ "data-testid": testId } as never}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        >
          {optionsOf(definition).map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );

    case "multiselect": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <>
          <Autocomplete
            multiple
            size="small"
            disabled={readOnly}
            options={optionsOf(definition)}
            value={optionsOf(definition).filter((o) => selected.includes(o.id))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, v) => option.id === v.id}
            onChange={(_, next) => onChange(next.map((o) => o.id))}
            renderOption={(props, option) => (
              <li {...props} data-testid={`${testId}-${option.id}`}>
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                // Stessa regola degli altri campi: la label sopra, non dentro.
                // L'`Autocomplete` costruisce la sua `InputLabel`, quindi lo
                // `shrink` va passato qui.
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                inputProps={{ ...params.inputProps, "data-testid": testId }}
              />
            )}
          />
          {errorBelow}
        </>
      );
    }

    // Il progressivo lo produce il server al consumo dell'operazione: qui si
    // mostra e basta.
    case "autoincrement":
      return (
        <TextField
          {...common}
          disabled
          value={typeof value === "string" ? value : ""}
          // Lo `shrink` della label arriva da `common`, come per ogni campo.
          // A processo avviato il campo porta il numero del registro. Prima
          // dell'avvio il processo non esiste e `start-context` e' `null` per
          // contratto: li' il segnaposto dice cosa succedera', senza promettere un
          // numero che il registro non ha ancora assegnato.
          placeholder={hasValue ? undefined : getTranslation(translations, "autoincrement.placeholder")}
          inputProps={{ "data-testid": testId }}
          // Il valore del progressivo lo assegna il server: qui non c'è
          // niente da mostrare oltre al valore stesso.
          helperText={undefined}
        />
      );

    // Un calcolato è del server: mostrarlo come scrivibile prometterebbe una
    // modifica che il server sovrascriverebbe. Ma si mostra **come gli altri
    // campi** — stesso riquadro, stessa etichetta sopra: una riga di testo nudo
    // accanto a cinque riquadri si legge come un'altra cosa, e chi guarda crede
    // che quel campo sia di natura diversa invece che di sola lettura.
    case "calculated":
      return (
        <TextField
          {...common}
          disabled
          value={
            error
              ? displayValue(value)
              : textForCalculatedValue(
                  value,
                  getTranslation(translations, CALCULATED_MISSING_DATA_KEY)
                )
          }
          inputProps={{ "data-testid": testId }}
        />
      );

    case "attachment": {
      // Il valore porta gli id: il nome lo risolve il controllo, dall'elenco
      // allegati del processo. Tollerante su una stringa sola perché la scelta
      // "uno o molti" è reversibile (vedi `attachment-field-design.md`).
      const ids = Array.isArray(value)
        ? value.map((id) => String(id))
        : typeof value === "string" && value !== ""
          ? [value]
          : [];

      return (
        <>
          <AttachmentFieldInput
            host={attachments}
            value={ids}
            onChange={(next) => onChange(next)}
            disabled={readOnly}
            label={label}
            testId={testId}
            // La matita di rinomina la concede il campo: la definizione la porta
            // nel contratto (`config.allowRename`), e la superficie del task non
            // ha un'altra fonte da cui deciderlo.
            allowRename={definition.config?.allowRename === true}
          />
          {errorBelow}
        </>
      );
    }

    default:
      // Un tipo che questo renderer non conosce non si inventa: si mostra il
      // valore in sola lettura, così il difetto è visibile invece che silenzioso.
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center" data-testid={testId}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Chip size="small" label={displayValue(value)} />
          </Stack>
          {errorBelow}
        </>
      );
  }
};

export default CustomFieldInput;
